import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { WA_LINK } from "../lib/data";
import { useLang } from "../lib/i18n";

export default function Nav() {
  const { lang, t, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("#home");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(`#${e.target.id}`);
        });
      },
      { rootMargin: "-35% 0px -55% 0px" }
    );
    t.navLinks.forEach(({ href }) => {
      const el = document.getElementById(href.slice(1));
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [t.navLinks]);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const langButton = (
    <button
      onClick={toggle}
      data-testid="lang-toggle-button"
      aria-label={lang === "en" ? "Switch to Urdu" : "Switch to English"}
      className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-2 font-display text-xs font-bold text-cream/85 transition-colors duration-300 hover:border-brass/50 hover:text-brasslight"
    >
      <Languages size={14} strokeWidth={2.4} />
      {lang === "en" ? "اردو" : "EN"}
    </button>
  );

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-ink/70 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.35)] border-b border-cream/5"
          : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a
          href="#home"
          onClick={(e) => goTo(e, "#home")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src="/assets/gt-logo.png"
            alt="Ghauri Traders logo"
            className="w-10 h-10 rounded-xl object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <span className="font-display tracking-tight text-lg font-bold">
            Ghauri <span className="text-brass">Traders</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {t.navLinks.map((link, i) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => goTo(e, link.href)}
              data-testid={`nav-link-${["home", "about", "products", "contact"][i]}`}
              className={`relative font-display text-sm font-semibold transition-colors duration-300 ${
                active === link.href ? "text-cream" : "text-taupe hover:text-cream"
              }`}
            >
              {link.label}
              {active === link.href && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute -bottom-2 left-0 right-0 h-[2px] rounded-full bg-brass"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
            </a>
          ))}
          {langButton}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-now-nav-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-brass px-5 py-2.5 font-display text-sm font-bold text-ink transition-all duration-300 hover:bg-brasslight hover:shadow-[0_8px_30px_rgba(200,184,154,0.35)] active:scale-95"
          >
            {t.navOrder} <ArrowUpRight size={16} strokeWidth={2.5} className="rtl:-scale-x-100" />
          </a>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          {langButton}
          <button
            className="text-cream p-2 -me-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-button"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden mx-4 mt-3 rounded-3xl bg-espresso/95 backdrop-blur-xl border border-cream/10 p-6 shadow-2xl"
            aria-label="Mobile"
            data-testid="mobile-menu-panel"
          >
            <div className="flex flex-col gap-1">
              {t.navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => goTo(e, link.href)}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.3 }}
                  data-testid={`mobile-nav-link-${["home", "about", "products", "contact"][i]}`}
                  className="font-display text-xl font-semibold py-3 border-b border-cream/5 text-cream/90"
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28 }}
                data-testid="mobile-order-now-button"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-brass px-6 py-3.5 font-display font-bold text-ink active:scale-95 transition-transform"
              >
                {t.navOrder} <ArrowUpRight size={18} strokeWidth={2.5} className="rtl:-scale-x-100" />
              </motion.a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
